import { useMemo, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import { SectionCard, milestoneMeta } from "../components/ui";
import { MilestoneDetailModal } from "../components/MilestoneDetailModal";
import { fmtDate } from "../utils";
import type { MilestoneState, PowerProject } from "../types";
import { useProcessGraph } from "./hooks";

const DAY = 24 * 3600 * 1000;

// Pasek harmonogramu w tych samych barwach i pod tą samą nazwą stanu co karta na
// tablicy kamieni i kafelek na grafie — jedna mapa stanów dla wszystkich widoków.
const stateBar = (state: string) => {
  const m = milestoneMeta(state as MilestoneState);
  return { bg: m.bg, border: m.accent, label: m.label };
};

const FLAG: Record<string, { color: Parameters<typeof Badge>[0]["color"]; label: string }> = {
  on_track: { color: "green", label: "Zdążysz" },
  at_risk: { color: "yellow", label: "Ryzyko" },
  will_miss: { color: "red", label: "Nie zdążysz" },
  unknown: { color: "gray", label: "—" },
};

/** Harmonogram (Gantt) z tej samej symulacji co graf DAG: daty start/koniec każdego
 * kroku (es/ef), ścieżka krytyczna na pomarańczowo, linia „dziś" i termin umowy.
 * Klik w pasek = ten sam modal szczegółów co na grafie. */
export function GanttView({ connection }: { connection: PowerProject }) {
  const { data, isLoading } = useProcessGraph(connection.id, false);
  // Klucz kroku klikniętego na pasku → ten sam modal szczegółów co na tablicy i grafie.
  const [selKey, setSelKey] = useState<string | null>(null);
  const selMilestone = selKey ? connection.milestones.find((m) => m.key === selKey) ?? null : null;

  const model = useMemo(() => {
    const nodes = (data?.nodes ?? []).filter((n) => n.es && n.ef && !n.is_wpw);
    if (!nodes.length) return null;
    const starts = nodes.map((n) => +new Date(n.es!));
    const ends = nodes.map((n) => +new Date(n.ef!));
    let min = Math.min(...starts, Date.now());
    let max = Math.max(...ends, Date.now());
    if (data?.contract_deadline) max = Math.max(max, +new Date(data.contract_deadline));
    // margines, żeby paski nie dotykały krawędzi
    const span = Math.max(max - min, 7 * DAY);
    min -= span * 0.02;
    max += span * 0.04;
    const pct = (t: number) => ((t - min) / (max - min)) * 100;
    // podziałka miesięczna
    const months: { left: number; label: string }[] = [];
    const d = new Date(min);
    d.setDate(1);
    d.setMonth(d.getMonth() + 1);
    while (+d < max) {
      months.push({ left: pct(+d), label: d.toLocaleDateString("pl-PL", { month: "short", year: "2-digit" }) });
      d.setMonth(d.getMonth() + 1);
    }
    // sort: start rosnąco, potem koniec
    const rows = [...nodes].sort((a, b) => +new Date(a.es!) - +new Date(b.es!) || +new Date(a.ef!) - +new Date(b.ef!));
    return { rows, pct, months, min, max };
  }, [data]);

  const flag = FLAG[data?.deadline_flag ?? "unknown"] ?? FLAG.unknown;

  return (
    <SectionCard
      title="Harmonogram — te same daty co na grafie (symulacja przepływu)"
      action={data && (
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[var(--color-text-muted)]">Zakończenie:</span>
          <span className="font-mono">{data.completion_date ? fmtDate(data.completion_date) : "—"}</span>
          <Badge color={flag.color}>{flag.label}</Badge>
        </div>
      )}
    >
      {isLoading || !model ? (
        <div className="text-sm text-[var(--color-text-muted)]">{isLoading ? "Ładowanie…" : "Brak danych do harmonogramu."}</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[720px]">
            {/* oś: miesiące + linia dziś + termin umowy */}
            <div className="relative h-6 ml-[240px] border-b border-[var(--color-border)]">
              {model.months.map((m, i) => (
                <span key={i} className="absolute top-0 text-[9px] font-mono text-[var(--color-text-muted)] border-l border-[var(--color-border)] pl-0.5 h-full"
                  style={{ left: `${m.left}%` }}>{m.label}</span>
              ))}
            </div>
            <div className="relative">
              {/* linie pionowe przez całą wysokość */}
              <div className="absolute inset-y-0 ml-[240px] right-0 left-[240px] pointer-events-none">
                {model.months.map((m, i) => (
                  <span key={i} className="absolute inset-y-0 border-l border-[var(--color-border)]/60" style={{ left: `${m.left}%` }} />
                ))}
                <span className="absolute inset-y-0 border-l-2 border-dashed border-red-500" style={{ left: `${model.pct(Date.now())}%` }} title="Dziś" />
                {data?.contract_deadline && (
                  <span className="absolute inset-y-0 border-l-2 border-[#7c3aed]" style={{ left: `${model.pct(+new Date(data.contract_deadline))}%` }}
                    title={`Termin umowy: ${fmtDate(data.contract_deadline)}`} />
                )}
              </div>
              {model.rows.map((n) => {
                const st = stateBar(n.state);
                const left = model.pct(+new Date(n.es!));
                const width = Math.max(model.pct(+new Date(n.ef!)) - left, 0.6);
                const active = (n.phases ?? []).find((p) => p.status === "active");
                return (
                  <div key={n.key} className="flex items-center h-8 border-b border-[var(--color-border)]/40">
                    <div className="w-[240px] shrink-0 pr-2 text-xs truncate" title={n.label}>
                      {n.is_critical && <span style={{ color: "#ea580c" }} title="Ścieżka krytyczna">◆ </span>}
                      {n.is_manual && <span title="Krok dodany ręcznie">✎ </span>}
                      <b>{n.label}</b>
                    </div>
                    <div className="relative flex-1 h-full">
                      <button
                        type="button"
                        onClick={() => { if (!n.expandable) setSelKey(n.key); }}
                        className="absolute top-1.5 h-5 rounded text-[9px] font-mono overflow-hidden whitespace-nowrap px-1 text-left"
                        style={{
                          left: `${left}%`, width: `${width}%`,
                          background: st.bg,
                          border: `1.5px ${n.is_manual ? "dashed" : "solid"} ${n.is_critical ? "#ea580c" : st.border}`,
                          cursor: n.expandable ? "default" : "pointer",
                        }}
                        title={`${n.label}: ${fmtDate(n.es!)} → ${fmtDate(n.ef!)} · ${st.label}`
                          + (active ? `\nTeraz: ${active.label}` : "")}
                      >
                        {st.label}{active ? ` · ${active.label}` : ""}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="text-[10px] font-mono text-[var(--color-text-muted)] mt-2 flex flex-wrap gap-3">
              <span style={{ color: "#ef4444" }}>┆ dziś</span>
              {data?.contract_deadline && <span style={{ color: "#7c3aed" }}>│ termin umowy {fmtDate(data.contract_deadline)}</span>}
              <span style={{ color: "#ea580c" }}>◆ ścieżka krytyczna</span>
              <span>klik w pasek = szczegóły kroku</span>
            </div>
          </div>
        </div>
      )}
      {selMilestone && (
        <MilestoneDetailModal
          connection={connection}
          milestone={selMilestone}
          onClose={() => setSelKey(null)}
        />
      )}
    </SectionCard>
  );
}
